import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Loader2, User, Mail, Phone, Building2, Lock, FileText, Award, Users, MapPin, Globe, Briefcase, Lightbulb, Tag, BarChart3 } from 'lucide-react';
import { usersAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const UserProfile = () => {
  const { language } = useLanguage();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    bio: '',
    city: '',
    country: '',
    region: '',
    valueProposition: '',
    challengeCategory: '',
    developmentStage: '',
    website: '',
    jobTitle: '',
  });

  const t = {
    es: {
      title: 'Mi Perfil',
      subtitle: 'Administra tu información personal',
      personalInfo: 'Información Personal',
      extendedInfo: 'Información Adicional',
      name: 'Nombre',
      email: 'Correo electrónico',
      phone: 'Teléfono',
      organization: 'Empresa / Startup',
      bio: 'Descripción',
      bioPlaceholder: 'Cuéntanos sobre tu proyecto...',
      city: 'Ciudad',
      country: 'País',
      region: 'Región',
      valueProposition: 'Propuesta de Valor',
      valuePlaceholder: 'Describe tu propuesta de valor...',
      challengeCategory: 'Categoría de Desafío',
      developmentStage: 'Estado de Desarrollo',
      website: 'Sitio Web',
      jobTitle: 'Cargo',
      role: 'Rol',
      roles: {
        admin: 'Administrador',
        applicant: 'Postulante',
        organization: 'Organización',
        evaluator: 'Evaluador'
      },
      changePassword: 'Cambiar Contraseña',
      currentPassword: 'Contraseña Actual',
      newPassword: 'Nueva Contraseña',
      confirmPassword: 'Confirmar Nueva Contraseña',
      passwordMismatch: 'Las contraseñas no coinciden',
      save: 'Guardar Cambios',
      cancel: 'Cancelar',
      success: 'Perfil actualizado correctamente',
      passwordSuccess: 'Contraseña actualizada correctamente',
      error: 'Error al actualizar',
      stats: 'Estadísticas',
      applications: 'Postulaciones',
      assignments: 'Asignaciones',
      scores: 'Evaluaciones',
      programs: 'Desafíos creados'
    },
    en: {
      title: 'My Profile',
      subtitle: 'Manage your personal information',
      personalInfo: 'Personal Information',
      extendedInfo: 'Additional Information',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      organization: 'Company / Startup',
      bio: 'Description',
      bioPlaceholder: 'Tell us about your project...',
      city: 'City',
      country: 'Country',
      region: 'Region',
      valueProposition: 'Value Proposition',
      valuePlaceholder: 'Describe your value proposition...',
      challengeCategory: 'Challenge Category',
      developmentStage: 'Development Stage',
      website: 'Website',
      jobTitle: 'Job Title',
      role: 'Role',
      roles: {
        admin: 'Administrator',
        applicant: 'Applicant',
        organization: 'Organization',
        evaluator: 'Evaluator'
      },
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm New Password',
      passwordMismatch: 'Passwords do not match',
      save: 'Save Changes',
      cancel: 'Cancel',
      success: 'Profile updated successfully',
      passwordSuccess: 'Password updated successfully',
      error: 'Error updating',
      stats: 'Statistics',
      applications: 'Applications',
      assignments: 'Assignments',
      scores: 'Evaluations',
      programs: 'Challenges created'
    }
  }[language];

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const userId = user._id || user.id;
      const data = await usersAPI.getProfile(userId);
      setProfile(data);
      setFormData({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        organization: data.organization || '',
        bio: data.bio || '',
        city: data.city || '',
        country: data.country || '',
        region: data.region || '',
        valueProposition: data.valueProposition || '',
        challengeCategory: data.challengeCategory || '',
        developmentStage: data.developmentStage || '',
        website: data.website || '',
        jobTitle: data.jobTitle || '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast({ title: t.error, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const userId = user._id || user.id;
      await usersAPI.update(userId, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        organization: formData.organization || null,
        bio: formData.bio || null,
        city: formData.city || null,
        country: formData.country || null,
        region: formData.region || null,
        valueProposition: formData.valueProposition || null,
        challengeCategory: formData.challengeCategory || null,
        developmentStage: formData.developmentStage || null,
        website: formData.website || null,
        jobTitle: formData.jobTitle || null,
      });
      toast({ title: t.success });
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({ 
        title: t.error, 
        description: error.response?.data?.detail || '',
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({ title: t.passwordMismatch, variant: 'destructive' });
      return;
    }
    
    try {
      setSaving(true);
      const userId = user._id || user.id;
      await usersAPI.updatePassword(userId, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      toast({ title: t.passwordSuccess });
      setPasswordDialogOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error updating password:', error);
      toast({ 
        title: t.error, 
        description: error.response?.data?.detail || '',
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'organization': return 'bg-blue-100 text-blue-800';
      case 'evaluator': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
          <p className="text-gray-600 mt-1">{t.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {t.personalInfo}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Role Badge */}
              <div>
                <Label>{t.role}</Label>
                <div className="mt-1">
                  <Badge className={getRoleBadgeStyle(profile?.role)}>
                    {t.roles[profile?.role] || profile?.role}
                  </Badge>
                </div>
              </div>
              
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-1">
                  <User className="w-4 h-4" /> {t.name}
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-1">
                  <Mail className="w-4 h-4" /> {t.email}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              
              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-1">
                  <Phone className="w-4 h-4" /> {t.phone}
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              
              {/* Organization */}
              <div className="space-y-2">
                <Label htmlFor="organization" className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" /> {t.organization}
                </Label>
                <Input
                  id="organization"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                />
              </div>
              
              {/* Bio */}
              <div className="space-y-2">
                <Label htmlFor="bio">{t.bio}</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder={t.bioPlaceholder}
                  rows={3}
                />
              </div>

              {/* Extended fields for applicants */}
              {profile?.role === 'applicant' && (
                <>
                  <div className="border-t border-gray-200 pt-6 mt-2">
                    <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" /> {t.extendedInfo}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="jobTitle" className="flex items-center gap-1">
                        <Briefcase className="w-3.5 h-3.5" /> {t.jobTitle}
                      </Label>
                      <Input
                        id="jobTitle"
                        value={formData.jobTitle}
                        onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website" className="flex items-center gap-1">
                        <Globe className="w-3.5 h-3.5" /> {t.website}
                      </Label>
                      <Input
                        id="website"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        placeholder="https://"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city" className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" /> {t.city}
                      </Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="region">{t.region}</Label>
                      <Input
                        id="region"
                        value={formData.region}
                        onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">{t.country}</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="challengeCategory" className="flex items-center gap-1">
                        <Tag className="w-3.5 h-3.5" /> {t.challengeCategory}
                      </Label>
                      <Input
                        id="challengeCategory"
                        value={formData.challengeCategory}
                        onChange={(e) => setFormData({ ...formData, challengeCategory: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="developmentStage" className="flex items-center gap-1">
                        <BarChart3 className="w-3.5 h-3.5" /> {t.developmentStage}
                      </Label>
                      <Input
                        id="developmentStage"
                        value={formData.developmentStage}
                        onChange={(e) => setFormData({ ...formData, developmentStage: e.target.value })}
                        placeholder="Idea / MVP / Growth"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="valueProposition" className="flex items-center gap-1">
                      <Lightbulb className="w-3.5 h-3.5" /> {t.valueProposition}
                    </Label>
                    <Textarea
                      id="valueProposition"
                      value={formData.valueProposition}
                      onChange={(e) => setFormData({ ...formData, valueProposition: e.target.value })}
                      placeholder={t.valuePlaceholder}
                      rows={3}
                    />
                  </div>
                </>
              )}
              
              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t.save}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setPasswordDialogOpen(true)}
                  className="border-purple-300 text-purple-600 hover:bg-purple-50"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {t.changePassword}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Stats Card */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t.stats}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile?.role === 'applicant' && (
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-600" />
                      <span className="text-sm text-gray-700">{t.applications}</span>
                    </div>
                    <span className="text-xl font-bold text-purple-600">
                      {profile?.applicationCount || 0}
                    </span>
                  </div>
                )}
                
                {profile?.role === 'evaluator' && (
                  <>
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-yellow-600" />
                        <span className="text-sm text-gray-700">{t.assignments}</span>
                      </div>
                      <span className="text-xl font-bold text-yellow-600">
                        {profile?.assignmentCount || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Award className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-gray-700">{t.scores}</span>
                      </div>
                      <span className="text-xl font-bold text-green-600">
                        {profile?.scoreCount || 0}
                      </span>
                    </div>
                  </>
                )}
                
                {profile?.role === 'organization' && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-gray-700">{t.programs}</span>
                    </div>
                    <span className="text-xl font-bold text-blue-600">
                      {profile?.programCount || 0}
                    </span>
                  </div>
                )}
                
                {profile?.role === 'admin' && (
                  <div className="text-sm text-gray-600 text-center p-4">
                    {language === 'es' 
                      ? 'Como administrador tienes acceso completo al sistema.' 
                      : 'As an administrator you have full system access.'}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Password Change Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t.changePassword}</DialogTitle>
              <DialogDescription>
                {language === 'es' 
                  ? 'Ingresa tu contraseña actual y la nueva contraseña.' 
                  : 'Enter your current password and new password.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t.currentPassword}</Label>
                <Input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t.newPassword}</Label>
                <Input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t.confirmPassword}</Label>
                <Input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                {t.cancel}
              </Button>
              <Button 
                onClick={handlePasswordChange} 
                disabled={saving || !passwordData.currentPassword || !passwordData.newPassword}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t.save}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default UserProfile;
